/**
 * Einladung annehmen (Welle 4) — `/einladung?token=...` aus der Einladungs-Mail.
 * Erzwingt zuerst ein Stack-Auth-Login (useUser({or:"redirect"}), wie ProtectedLayout), ruft
 * dann POST /api/invites/accept auf und leitet bei Erfolg auf das Dashboard weiter. Bewusst
 * AUSSERHALB von AppShell/ProtectedLayout: der Eingeladene ist zu diesem Zeitpunkt noch kein
 * Tenant-Mitglied, AppShell wuerde tenant-gebundene Daten laden, die fuer ihn 403 liefern.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useUser } from "@stackframe/react";
import { apiFetch, toApiError } from "../lib/api";
import { Icon } from "../ui/components";

type Status = "loading" | "ok" | "error";

export default function AcceptInvitePage() {
  useUser({ or: "redirect" });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Der Einladungslink ist unvollständig (kein Token).");
      return;
    }
    let alive = true;
    (async () => {
      try {
        const res = await apiFetch("/api/invites/accept", { method: "POST", body: JSON.stringify({ token }) });
        if (!res.ok) throw await toApiError(res);
        if (!alive) return;
        setStatus("ok");
        setTimeout(() => navigate("/"), 1500);
      } catch (err) {
        if (!alive) return;
        setStatus("error");
        setMessage(err instanceof Error ? err.message : "Einladung konnte nicht angenommen werden.");
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 font-body-md text-on-surface">
      <div className="w-full max-w-md rounded-xl bg-surface-container-lowest p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
        {status === "loading" && (
          <>
            <Icon name="hourglass_top" className="mb-3 text-4xl text-primary" />
            <p className="font-body-md text-body-md text-on-surface-variant">Einladung wird angenommen …</p>
          </>
        )}
        {status === "ok" && (
          <>
            <Icon name="check_circle" className="mb-3 text-4xl text-secondary dark:text-[#b8ae9e]" />
            <p className="font-headline-sm text-headline-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Willkommen bei MAESTRO!
            </p>
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">Sie werden weitergeleitet …</p>
          </>
        )}
        {status === "error" && (
          <>
            <Icon name="error" className="mb-3 text-4xl text-error" />
            <p className="font-headline-sm text-headline-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Einladung ungültig
            </p>
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">{message}</p>
          </>
        )}
      </div>
    </div>
  );
}
